import React, {
  ForwardedRef,
  forwardRef,
  PropsWithChildren,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react'

import CrossLarge from '@helpscout/hsds-icons/cross-large'
import classNames from 'classnames'

import {
  ANIMATION_DURATION_MS,
  useAnimatedRender,
  useClickOutside,
  useRestoreFocusOnClose,
} from 'hsds/hooks'
import { useZIndex, ZIndexStackProvider } from 'hsds/hooks/zindex'
import { manageTrappedFocus } from 'hsds/utils/focus'
import { getValidProps } from 'hsds/utils/react'
import { CSSPropertiesWithVariables } from 'hsds/utils/types/CSSPropertiesWithVariables'

import { shouldUnsetDimensions, useModalAutoFocus } from './SimpleModal.utils'

import {
  CloseModalButtonUI,
  SimpleModalOverlayUI,
  SimpleModalUI,
} from './SimpleModal.styles'

import type { ExtraRestProps, SimpleModalBaseProps } from './SimpleModal.types'

const noop = () => {}

const DATA_COMPONENTS_ID = {
  OVERLAY: 'simple-modal-overlay',
  MODAL: 'simple-modal',
} as const

function SimpleModal({
  ariaLabelledBy = '',
  blocksGlobalHotkeys = true,
  children,
  className,
  customCloseButton,
  closeOnClickOutside = false,
  'data-cy': dataCy = 'SimpleModal',
  focusModalOnShow = true,
  focusTargetElement = null,
  onModalOpen = noop,
  onClose = noop,
  show = false,
  trapFocus = true,
  width = '360px',
  height = '390px',
  unsetDimensions = false,
  withCloseButton = true,
  zIndex,
  fullscreen = false,
  ...rest
}: SimpleModalBaseProps) {
  const overlayRef = useRef<HTMLDivElement>(null)
  const modalRef = useRef<HTMLDivElement>(null)
  const [shouldRender, onAnimationEnd] = useAnimatedRender(show, overlayRef)

  useRestoreFocusOnClose(show, modalRef)
  useModalAutoFocus({
    modalRef,
    shouldRender,
    show,
    focusTargetElement,
    focusModalOnShow,
  })

  // Consumers can force the aria-hidden attribute via the `aria-hidden`
  // (kebab-case, HTMLAttributes) or `ariaHidden` (camelCase, legacy) prop.
  const ariaHiddenOverride =
    (rest as ExtraRestProps).ariaHidden ??
    (rest as Record<string, unknown>)['aria-hidden']

  // A closing modal is hidden with `inert` (below), never with aria-hidden=true:
  // aria-hidden on an ancestor of the focused element makes browsers log
  // "Blocked aria-hidden on an element because its descendant retained focus".
  // Only an explicit override is reflected as aria-hidden.
  const isHidden =
    ariaHiddenOverride != null ? Boolean(ariaHiddenOverride) : !show

  const ariaHidden =
    ariaHiddenOverride != null ? Boolean(ariaHiddenOverride) : false

  // `inert` drops the subtree from the a11y tree and blurs a focused descendant
  // without a programmatic focus() (which would trigger refetchOnWindowFocus).
  // React 17 does not render the attribute, so it goes on the DOM node.
  // https://github.com/facebook/react/issues/17157
  useLayoutEffect(() => {
    if (modalRef.current == null) return

    if (isHidden) {
      modalRef.current.setAttribute('inert', '')
    } else {
      modalRef.current.removeAttribute('inert')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHidden, shouldRender])

  // shouldRender stays true through a reopen during the exit animation
  const isOpen = shouldRender && show

  useEffect(() => {
    if (!isOpen) return

    const timeout = setTimeout(
      () => onModalOpen(modalRef),
      ANIMATION_DURATION_MS
    )

    return () => clearTimeout(timeout)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const pressTargetRef = useRef<EventTarget | null>(null)

  useClickOutside(
    closeOnClickOutside === 'overlay' && shouldRender ? overlayRef : null,
    onClose
  )

  function handleOverlayMouseDown(e: React.MouseEvent) {
    pressTargetRef.current = e.target
  }

  // Nested modals, portals and editor toolbars never have the overlay as
  // target, so `target === currentTarget` is the whole backdrop test. A press
  // that starts inside the modal and releases on the backdrop (a text
  // selection drag) must not close it.
  function handleOverlayClick(e: React.MouseEvent) {
    const pressStartedInside =
      pressTargetRef.current !== null &&
      pressTargetRef.current !== e.currentTarget
    pressTargetRef.current = null

    if (closeOnClickOutside !== 'modal') return
    if (e.target !== e.currentTarget || pressStartedInside) return

    onClose(e)
  }

  function handleOverlayKeyDown(e: React.KeyboardEvent) {
    // A nested modal that does not block hotkeys lets its keydown bubble here
    if (e.target instanceof Element) {
      const nearestOverlay = e.target.closest(
        `[data-component-id="${DATA_COMPONENTS_ID.OVERLAY}"]`
      )

      if (nearestOverlay && nearestOverlay !== overlayRef.current) return
    }

    if (blocksGlobalHotkeys) {
      e.stopPropagation()
    }

    if (shouldRender && e.key === 'Escape') {
      onClose(e)
    } else if (e.key === 'Tab' && trapFocus && modalRef.current) {
      manageTrappedFocus(modalRef.current, e)
    }
  }

  function renderCloseButton() {
    if (withCloseButton) {
      if (customCloseButton && React.isValidElement(customCloseButton)) {
        const clickHandler = customCloseButton.props.onClick || onClose

        return React.cloneElement(customCloseButton, {
          onClick: clickHandler,
        })
      }

      return (
        <CloseModalButtonUI
          aria-label="close modal button"
          className="SimpleModal__CloseButton"
          onClick={(e: React.MouseEvent | MouseEvent) => {
            e.stopPropagation()
            onClose(e)
          }}
          color="white"
          icon={CrossLarge}
          size="md"
        />
      )
    }

    return null
  }

  const clearDimensions = shouldUnsetDimensions(unsetDimensions, children)

  const shouldSetWidth = !clearDimensions && width !== '360px'
  const shouldSetHeight = !clearDimensions && height !== '390px'

  // Use useZIndex to resolve the z-index, accounting for parent contexts
  const resolvedZIndex = useZIndex({ zIndex }, 'modal')

  return shouldRender ? (
    <ZIndexStackProvider context="modal" zIndex={resolvedZIndex}>
      <SimpleModalOverlay
        className={classNames(
          'SimpleModal__Overlay',
          show && 'element-in',
          fullscreen && 'is-fullscreen',
          className
        )}
        data-component-id={DATA_COMPONENTS_ID.OVERLAY}
        data-testid={DATA_COMPONENTS_ID.OVERLAY}
        onAnimationEnd={e => {
          e.stopPropagation()
          onAnimationEnd(e)
        }}
        onKeyDown={handleOverlayKeyDown}
        onMouseDown={handleOverlayMouseDown}
        onClick={handleOverlayClick}
        ref={overlayRef}
        zIndex={resolvedZIndex}
      >
        <SimpleModalUI
          aria-hidden={ariaHidden}
          aria-modal="true"
          aria-labelledby={ariaLabelledBy}
          className={classNames(
            'SimpleModal',
            clearDimensions && 'cleared-dimensions'
          )}
          data-cy={dataCy}
          data-component-id={DATA_COMPONENTS_ID.MODAL}
          data-testid={DATA_COMPONENTS_ID.MODAL}
          data-blocks-global-hotkeys={blocksGlobalHotkeys}
          id={DATA_COMPONENTS_ID.MODAL}
          role="dialog"
          ref={modalRef}
          style={
            {
              ...(shouldSetHeight && { '--hsds-simpleModal-height': height }),
              ...(shouldSetWidth && { '--hsds-simpleModal-width': width }),
            } as React.CSSProperties
          }
          tabIndex={0}
          {...getValidProps(rest)}
        >
          {renderCloseButton()}
          {children}
        </SimpleModalUI>
      </SimpleModalOverlay>
    </ZIndexStackProvider>
  ) : null
}

const SimpleModalOverlay = forwardRef(function SimpleModalOverlay(
  {
    children,
    zIndex,
    ...props
  }: PropsWithChildren<
    Omit<SimpleModalBaseProps, 'zIndex'> & {
      zIndex: number | string | undefined
    }
  >,
  ref: ForwardedRef<HTMLDivElement>
) {
  return (
    <SimpleModalOverlayUI
      {...getValidProps(props)}
      ref={ref}
      style={
        {
          ...(zIndex ? { '--hsds-simpleModal-overlay-z-index': zIndex } : {}),
        } as CSSPropertiesWithVariables
      }
    >
      {children}
    </SimpleModalOverlayUI>
  )
})

export default SimpleModal
